import { JUTRO_TOPICS, publish } from '@jutro/events';
import {
    ADOBE_TOPICS,
    COMMON_TAGGING_PROPERTIES,
    SgSessionStorageService,
    SgSubmissionUtil,
    SMI_TAGGING_PROPERTIES
} from 'saga-modules';
import SgSmiUtils from 'sg-smi-utils';
import SmiConstants from 'sg-smi-constants';
import CommonConstants from 'sg-common-constants';

const setScidBasedOnSmiURL = () => {
    if ((window.location.hash.indexOf('scid') !== -1)) {
        const scid = decodeURIComponent(window.location.hash.split('scid=')[1].split('&')[0]);
        publish(ADOBE_TOPICS.ADOBE_EVENT, {
            adobeID: COMMON_TAGGING_PROPERTIES.scid,
            value: scid
        });
    }
};

const setCoverLevelMonthlyPrices = (modelVM, coverLevel) => {
    publish(ADOBE_TOPICS.ADOBE_EVENT, {
        adobeID: SMI_TAGGING_PROPERTIES[SmiConstants.MONTHLY_PRICE_NAMES[coverLevel]],
        value: SgSmiUtils.getMonthlyPriceByCoverLevel(modelVM, coverLevel)
    });
};

const setCoverLevelQuarterlyPrices = (modelVM, coverLevel) => {
    publish(ADOBE_TOPICS.ADOBE_EVENT, {
        adobeID: SMI_TAGGING_PROPERTIES[SmiConstants.QUARTERLY_PRICE_NAMES[coverLevel]],
        value: SgSmiUtils.getQuarterlyPriceByCoverLevel(modelVM, coverLevel)
    });
};

const setTotalPremiumCost = (modelVM) => {
    publish(ADOBE_TOPICS.ADOBE_EVENT, {
        adobeID: SMI_TAGGING_PROPERTIES.totalPremiumCost,
        value: SgSmiUtils.getMonthlyTotalPayable(modelVM)
    });
};

const computePriceDifference = (modelVM) => {
    // see PD-586 [https://sagainsurance.atlassian.net/browse/PD-586] for details of algorithm under priceDifference
    const selectedCoverLevel = SgSmiUtils.getSelectedCoverLevel(modelVM);
    const selectedCoverLevelPrice = SgSmiUtils.getMonthlyPriceByCoverLevel(modelVM, selectedCoverLevel);
    const allCoverLevels = Object.values(SmiConstants.COVER_LEVEL_CODES);
    const notSelectedCoverLevels = allCoverLevels.filter((arrayItem) => arrayItem !== selectedCoverLevel);
    const notSelectedCoverLevelPrices = notSelectedCoverLevels.map((coverLevel) => SgSmiUtils.getMonthlyPriceByCoverLevel(modelVM, coverLevel));
    const diffSelectedAndMaxPrices = selectedCoverLevelPrice - Math.max(...notSelectedCoverLevelPrices);
    const diffSelectedAndMinPrices = selectedCoverLevelPrice - Math.min(...notSelectedCoverLevelPrices);
    return `${diffSelectedAndMaxPrices.toFixed(2)}|${diffSelectedAndMinPrices.toFixed(2)}`;
};

const setPriceDifference = (modelVM) => {
    publish(ADOBE_TOPICS.ADOBE_EVENT, {
        adobeID: SMI_TAGGING_PROPERTIES.priceDifference,
        value: computePriceDifference(modelVM)
    });
};

const setAddOnsPmi = (modelVM) => {
    const result = [];
    const smiInsurance = modelVM.lobData.privateMedical.coverables.smiInsurance.value;
    const extendedCancerCover = smiInsurance.hadAddedExtendedCancerCover;
    const noClaimDiscountProtection = smiInsurance.hasAddedNCDProtection;
    const healthCashBenefitsCover = smiInsurance.hasAddedHealthCashBenefits;
    const hypertensionAddon = smiInsurance.hadAddedHighBloodPressure;
    if (extendedCancerCover) {
        result.push({ addonProduct: SmiConstants.SMI_ADDON_ACRONYMS.EXTENDED_CANCER_COVER });
    }
    if (noClaimDiscountProtection) {
        result.push({ addonProduct: SmiConstants.SMI_ADDON_ACRONYMS.NO_CLAIM_DISCOUNT_PROTECTION });
    }
    if (healthCashBenefitsCover) {
        result.push({ addonProduct: SmiConstants.SMI_ADDON_ACRONYMS.HEALTH_CASH_BENEFITS_COVER });
    }
    if (hypertensionAddon) {
        result.push({ addonProduct: SmiConstants.SMI_ADDON_ACRONYMS.HIGH_BLOOD_PRESSURE_ADDON });
    }
    publish(ADOBE_TOPICS.ADOBE_EVENT, {
        adobeID: SMI_TAGGING_PROPERTIES.addOnsPmi,
        value: result
    });
};

const setRemovedAddOns = (modelVM) => {
    const result = [];
    const key = SgSessionStorageService.KEY.ADDON_STATE;
    const state = JSON.parse(SgSessionStorageService.getItem(key));
    const smiInsurance = modelVM.lobData.privateMedical.coverables.smiInsurance.value;
    const extendedCancerCover = smiInsurance.hadAddedExtendedCancerCover;
    const noClaimDiscountProtection = smiInsurance.hasAddedNCDProtection;
    const healthCashBenefitsCover = smiInsurance.hasAddedHealthCashBenefits;
    const hypertensionAddon = smiInsurance.hadAddedHighBloodPressure;
    if (state[SmiConstants.SMI_ADDON_REMOVED_NAMES.EXTENDED_CANCER_COVER] && !extendedCancerCover) {
        result.push({ addonProduct: SmiConstants.SMI_ADDON_ACRONYMS.EXTENDED_CANCER_COVER });
    }
    if (state[SmiConstants.SMI_ADDON_REMOVED_NAMES.NO_CLAIM_DISCOUNT_PROTECTION] && !noClaimDiscountProtection) {
        result.push({ addonProduct: SmiConstants.SMI_ADDON_ACRONYMS.NO_CLAIM_DISCOUNT_PROTECTION });
    }
    if (state[SmiConstants.SMI_ADDON_REMOVED_NAMES.HEALTH_CASH_BENEFITS_COVER] && !healthCashBenefitsCover) {
        result.push({ addonProduct: SmiConstants.SMI_ADDON_ACRONYMS.HEALTH_CASH_BENEFITS_COVER });
    }
    if (state[SmiConstants.SMI_ADDON_REMOVED_NAMES.HIGH_BLOOD_PRESSURE_ADDON] && !hypertensionAddon) {
        result.push({ addonProduct: SmiConstants.SMI_ADDON_ACRONYMS.HIGH_BLOOD_PRESSURE_ADDON });
    }
    publish(ADOBE_TOPICS.ADOBE_EVENT, {
        adobeID: SMI_TAGGING_PROPERTIES.removedAddOns,
        value: result
    });
};

const initAddonState = () => {
    const key = SgSessionStorageService.KEY.ADDON_STATE;
    const state = {
        [SmiConstants.SMI_ADDON_TRACKING_NAMES.EXTENDED_CANCER_COVER]: [],
        [SmiConstants.SMI_ADDON_REMOVED_NAMES.EXTENDED_CANCER_COVER]: false,
        [SmiConstants.SMI_ADDON_TRACKING_NAMES.NO_CLAIM_DISCOUNT_PROTECTION]: [],
        [SmiConstants.SMI_ADDON_REMOVED_NAMES.NO_CLAIM_DISCOUNT_PROTECTION]: false,
        [SmiConstants.SMI_ADDON_TRACKING_NAMES.HEALTH_CASH_BENEFITS_COVER]: [],
        [SmiConstants.SMI_ADDON_REMOVED_NAMES.HEALTH_CASH_BENEFITS_COVER]: false,
        [SmiConstants.SMI_ADDON_TRACKING_NAMES.HIGH_BLOOD_PRESSURE_ADDON]: [],
        [SmiConstants.SMI_ADDON_REMOVED_NAMES.HIGH_BLOOD_PRESSURE_ADDON]: false
    };
    SgSessionStorageService.setItem(SgSessionStorageService.KEY.ADDON_STATE_INIT, true);
    SgSessionStorageService.setItem(key, JSON.stringify(state));
};

const addAddonState = (addonName, value) => {
    const key = SgSessionStorageService.KEY.ADDON_STATE;
    const state = JSON.parse(SgSessionStorageService.getItem(key));
    const addonTrackingName = SmiConstants.SMI_ADDON_TRACKING_NAMES[addonName];
    const addonRemovedName = SmiConstants.SMI_ADDON_REMOVED_NAMES[addonName];
    if (value === true || value === false) {
        state[addonTrackingName] = [...state[addonTrackingName], value];
    }
    // Prune addon selection state to last two operations
    if (state[addonTrackingName].length >= 2) {
        state[addonTrackingName] = state[addonTrackingName].slice(-2);
    }
    const addonStackHead = (state[addonTrackingName].length - 1);
    // Marks addon as removed, by checking for selection transition from true to false
    if (state[addonTrackingName][addonStackHead] === false
        && state[addonTrackingName][(addonStackHead - 1)] === true) {
        state[addonRemovedName] = true;
    }
    // Marks addon as added, by checking for selection transition from false to true
    if (state[addonTrackingName][addonStackHead] === true
        && state[addonTrackingName][(addonStackHead - 1)] === false) {
        state[addonRemovedName] = false;
    }
    SgSessionStorageService.setItem(key, JSON.stringify(state));
};

const trackAddonStateChange = (modelVM) => {
    const smiInsurance = modelVM.lobData.privateMedical.coverables.smiInsurance.value;
    const extendedCancerCover = smiInsurance.hadAddedExtendedCancerCover;
    const noClaimDiscountProtection = smiInsurance.hasAddedNCDProtection;
    const healthCashBenefitsCover = smiInsurance.hasAddedHealthCashBenefits;
    const hypertensionAddon = smiInsurance.hadAddedHighBloodPressure;
    const key = SgSessionStorageService.KEY.ADDON_STATE;
    const state = JSON.parse(SgSessionStorageService.getItem(key));
    if (state === null || state === undefined) {
        initAddonState();
    }
    addAddonState(SmiConstants.SMI_ADDON_REVERSE_NAMES.EXTENDED_CANCER_COVER, extendedCancerCover);
    addAddonState(SmiConstants.SMI_ADDON_REVERSE_NAMES.NO_CLAIM_DISCOUNT_PROTECTION, noClaimDiscountProtection);
    addAddonState(SmiConstants.SMI_ADDON_REVERSE_NAMES.HEALTH_CASH_BENEFITS_COVER, healthCashBenefitsCover);
    addAddonState(SmiConstants.SMI_ADDON_REVERSE_NAMES.HIGH_BLOOD_PRESSURE_ADDON, hypertensionAddon);
};

const setPaymentByInstalement = (modelVM) => {
    const paymentPlanId = SgSubmissionUtil.getSelectedPaymentPlan(modelVM).billingId;
    let value;
    if (paymentPlanId === CommonConstants.PAYMENT_PLANS_ID.ANNUAL_UPFRONT
        || paymentPlanId === CommonConstants.PAYMENT_PLANS_ID.ANNUAL_DD) {
        value = false;
    }
    if (paymentPlanId === CommonConstants.PAYMENT_PLANS_ID.MONTHLY_DD
        || paymentPlanId === CommonConstants.PAYMENT_PLANS_ID.QUARTERLY_DD) {
        value = true;
    }
    publish(ADOBE_TOPICS.ADOBE_EVENT, {
        adobeID: SMI_TAGGING_PROPERTIES.paymentByInstalments,
        value: value
    });
};

const setStickyBannerCost = (modelVM) => {
    const selectedCoverLevel = SgSmiUtils.getSelectedCoverLevel(modelVM);
    const value = SgSmiUtils.getPriceForCoverLevel(modelVM, selectedCoverLevel);
    publish(ADOBE_TOPICS.ADOBE_EVENT, {
        adobeID: SMI_TAGGING_PROPERTIES.stickyBannerCost,
        value: value
    });
};

const setQuotePageTrackedPriceValues = (modelVM) => {
    setTotalPremiumCost(modelVM);
    setPriceDifference(modelVM);
    setStickyBannerCost(modelVM);
};
const getCoverLevelAdobeValue = (code) => {
    const productName = SmiConstants.PRODUCT_NAMES[code].replace(/\s/g, '');
    return `${SmiConstants.ADOBE_VALUES.QUOTE_SELECT_PLAN}${productName}`;
};

const trackPremiumPageActionsForRadioButtons = (adobeValue, value) => {
    publish(JUTRO_TOPICS.BUTTON_CLICKED, {
        adobeValue: `${adobeValue}${value ? SmiConstants.ADOBE_VALUES.YES : SmiConstants.ADOBE_VALUES.NO}`
    });
};

const getInsuredPersonAdobeValue = (suffix, isPartner) => {
    const personWording = isPartner ? SmiConstants.ADOBE_VALUES.PARTNER : SmiConstants.ADOBE_VALUES.DEPENDANT;
    return `${SmiConstants.ADOBE_VALUES.QUOTE_PEOPLE}${personWording}${suffix}`;
};

export default {
    setScidBasedOnSmiURL,
    setCoverLevelMonthlyPrices,
    setCoverLevelQuarterlyPrices,
    setPaymentByInstalement,
    setQuotePageTrackedPriceValues,
    setAddOnsPmi,
    setRemovedAddOns,
    initAddonState,
    trackAddonStateChange,
    getCoverLevelAdobeValue,
    trackPremiumPageActionsForRadioButtons,
    getInsuredPersonAdobeValue
};
